/**
 * Reading the payload of our own JSON Web Tokens - LIQUIDO's team token and Polly's session token alike.
 *
 * <b>None of this is a security boundary.</b> The browser has no key and cannot verify a signature,
 * and the token it is reading came out of its own localStorage anyway. Everything here decides what
 * the UI SHOWS, or whether a token is worth sending at all. Every actual permission is enforced by the
 * backend, which verifies the signature on every single request.
 *
 * In src/shared because both apps need it, and neither may import the other (docs/liquido-architecture.md §2.1).
 */

/**
 * Decode the payload of a JWT. Returns undefined for anything that is not a decodable JWT,
 * rather than throwing: a missing or malformed token simply means "no claims", and every caller
 * treats that as the least privileged answer.
 *
 * @param {String} jwt a JSON web token, "header.payload.signature"
 * @returns {Object} the decoded payload, or undefined
 */
export function decodeJwtPayload(jwt) {
	if (typeof jwt !== "string") return undefined
	const segments = jwt.split(".")
	if (segments.length !== 3) return undefined
	try {
		// base64url -> base64, then a UTF-8 safe atob: names and emails are not always ASCII.
		const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/")
		const json = decodeURIComponent(
			atob(base64)
				.split("")
				.map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
				.join("")
		)
		return JSON.parse(json)
	} catch (e) {
		console.warn("Cannot decode JWT payload", e)
		return undefined
	}
}

/**
 * Has this JWT run out? Reads its "exp" claim (seconds since the epoch).
 *
 * A token without a readable exp is NOT called expired: that is the mocks' made-up tokens, and for a real
 * one the backend has the last word anyway (HTTP 401).
 *
 * @param {String} jwt a JSON web token
 * @param {Number} nowMs the current time, in milliseconds - for tests
 * @returns {Boolean} true when the token has an exp claim and it is in the past
 */
export function isJwtExpired(jwt, nowMs = Date.now()) {
	const exp = decodeJwtPayload(jwt)?.exp
	return typeof exp === "number" && exp * 1000 <= nowMs
}
